import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Lock, ArrowUpRight } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { openLeadForm } from "@/components/LeadFormDialog";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "About Us", href: "/about" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

function NavLink({ l, onClick }) {
  const cls = "text-sm font-medium text-[#5A6578] transition-colors duration-300 hover:text-[#0E8A8A]";
  const testId = `nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
  if (l.href.startsWith("/")) {
    return <Link to={l.href} data-testid={testId} className={cls} onClick={onClick}>{l.label}</Link>;
  }
  return <a href={l.href} data-testid={testId} className={cls} onClick={onClick}>{l.label}</a>;
}

export default function Navbar({ brand }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="glass fixed inset-x-0 top-0 z-50 border-b border-[#E2DDD5]"
      data-testid="navbar"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-brand">
          {brand.logoUrl ? (
            <img src={assetUrl(brand.logoUrl)} alt={brand.name} className="h-10 w-10 object-contain" data-testid="nav-logo-img" />
          ) : (
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[#B8860B] to-[#9A6A15] font-syne text-lg font-bold text-white" data-testid="nav-logo-mark">
              {brand.logoMark || "\u0950"}
            </span>
          )}
          <span className="font-syne text-lg font-bold tracking-tight text-[#0F1B2B]">{brand.name}</span>
        </Link>
        <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => <NavLink key={l.href + l.label} l={l} />)}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <Link to="/login" data-testid="nav-login-link"
            className="flex items-center gap-1.5 rounded-full border border-[#E2DDD5] bg-white px-4 py-2 text-sm font-medium text-[#1B3A5C] transition-colors duration-300 hover:border-[#0E8A8A]/50 hover:text-[#0E8A8A]">
            <Lock size={13} /> Retailer Login
          </Link>
          <button onClick={openLeadForm} data-testid="nav-cta"
            className="flex items-center gap-1.5 rounded-full bg-[#1B3A5C] px-5 py-2 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#0E8A8A] active:scale-95">
            Get Started <ArrowUpRight size={14} />
          </button>
        </div>
        <button className="lg:hidden text-[#0F1B2B]" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle" aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="glass border-t border-[#E2DDD5] px-5 py-4 lg:hidden" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <div key={l.href + l.label}>
              <NavLink l={l} onClick={() => setOpen(false)} />
            </div>
          ))}
          <Link to="/login" className="block py-2.5 text-sm font-medium text-[#0E8A8A]" data-testid="nav-login-link-mobile">Retailer Login</Link>
        </div>
      )}
    </motion.header>
  );
}
